import { take, tap } from 'rxjs/operators';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable } from 'rxjs';
import {
  ClaimFaultTypeState,
  ClaimFaultTypeStore,
  SelectionSummary,
  SummaryHeading,
} from './claim-fault-type.store';
import {
  Api,
  AppConfig,
  Claim,
  ClaimsAnalyticsService,
  ClaimTypeSelected,
  fadeInAnimation,
} from '@domgen/dgx-components';
import {
  ClaimFaultTypeFormHelperService,
  FormTypes,
  isStandardNetworkFaultClaim,
} from './claim-fault-type-form-helper.service';
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RepairDetailsComponentService } from '../../../services/repair-details-component.service';
import { RepairDetailsService } from '../../../services/repair-details.service';
import {
  ChatClientService,
  SpecialRoutingService,
} from '@domgen/data-access-claims';
import {
  FilterFixApplianceType,
  WhiteGoodApplianceNames,
} from './showBannerHelpers';
import { SmartFixAnalyticsService } from '@domgen/smart-fix/feature-chat';
import { isProduct } from './showBannerHelpers';
import { CONFIG } from '@domgen/dgx-fe-config';
import PossibleAnswer = Api.PossibleAnswer;
@Component({
  selector: 'claims-claim-type-fault-type-form',
  templateUrl: './claim-fault-type-form.component.html',
  styleUrls: ['./claim-fault-type-form.component.scss'],
  animations: [fadeInAnimation],
})
export class ClaimFaultTypeFormComponent implements OnChanges {
  @ViewChild('optionContainer', { static: false }) optionsContainer: ElementRef;

  faultDataSearchForm = new FormGroup({
    search: new FormControl('', Validators.compose([Validators.required])),
  });

  freeTextSearch: boolean | undefined;

  standardNetworkFaultCapture = false;

  constructor(
    public cs: ClaimFaultTypeStore,
    public service: RepairDetailsComponentService,
    public repairDetailsService: RepairDetailsService,
    private routing: SpecialRoutingService,
    private analytics: ClaimsAnalyticsService,
    private chatService: ChatClientService,
    private readonly smartFixAnalyticsService: SmartFixAnalyticsService,
    @Inject(CONFIG) private readonly config: AppConfig,
    private claimFormTypeHelperService: ClaimFaultTypeFormHelperService
  ) {}

  public showInWarrantyNotice = false;
  public showplanInWaitPeriodNotice = false;
  public isIWEnabled = false;
  public InWarrantyContactName?: string;
  public InWarrantyContactTel?: string;
  public showSmartFixChatBot = false;
  public smartFixChatState: 'open' | 'closed' = 'open';
  public showPreChatBanner = false;
  public mode: string = '';
  claimType?: ClaimTypeSelected;
  claimSelection?: Api.ClaimType;
  position?: number;
  productType?: string;
  public readonly isProduct = isProduct;

  @Input() claim: Claim;
  @Output() claimTypeSelected: EventEmitter<ClaimTypeSelected> =
    new EventEmitter();

  @ViewChild('usabilityQuestion') usabilityQuestion: ElementRef;

  description = ``;
  cta = `Right, let’s continue`;

  formSubmitted = false;

  fallBackHappened = false;

  showFallbackAlert = false;
  claimTypeOptions;
  usabilityOptions;
  showUsabilityQuestion;
  continueClicked = false;

  answerSubmitted = false;
  showError = false;
  claimTypeAutoComplete;

  $vm = this.cs.vm$.pipe(
    tap((v) => {
      this.showFallbackAlert = false;
      this.standardNetworkFaultCapture = isStandardNetworkFaultClaim(v.claim);
      if (
        v.freeTextSearchState?.fallBackToOldSelect &&
        !this.fallBackHappened
      ) {
        this.fallBackHappened = true;
        this.cs.resetForm(
          v.selectionSummary,
          FormTypes.FAULTTYPEFORM,
          v.claim,
          false
        );
        setTimeout(() => {
          this.showFallbackAlert = true;
        }, 100);
      }

      if (v.formData?.freeTextSearch) {
        this.freeTextSearch = true;
      }

      const faultData = v.claim?.getData?.FaultData || [];
      this.faultOptionsList = Array.isArray(faultData)
        ? faultData
        : [faultData];

      this.setOptionContainerSize();

      if (v?.form?.controls) {
        this.continueClicked = !Object.keys(v.form?.controls)?.includes(
          'claimType'
        );

        const containsClaimTypeControl = Object.keys(
          v.form?.controls
        )?.includes('claimType');

        const hasMoreThanOneOptionForClaimType =
          (v.formData?.groups?.[0]?.radios?.radioGroup ?? []).length > 1;

        if (containsClaimTypeControl && !hasMoreThanOneOptionForClaimType) {
          v.form
            .get('claimType')
            .setValue(v.formData.groups[0].radios.radioGroup[0].value);
          this.showUsabilityQuestion = true;
          this.claimTypeAutoComplete = true;
        }

        if (this.showUsabilityQuestion && !v.form.get('ApplianceUseable')) {
          this.addUsabilityQuestion(v.form);
        }
      }
    })
  ) as Observable<ClaimFaultTypeState>;

  faultOptionsList: Api.FaultData[] = [];

  genericFaultListIconMap = {
    'cleaning performance': {
      icon: 'soap',
      colour: '#26A401',
    },
    'cooling and temperature': {
      icon: 'temperature-snow',
      colour: '#0065C4',
    },
    'detergent and conditioner': {
      icon: 'jug-detergent',
      colour: '#26A401',
    },
    'drying performance': {
      icon: 'heat',
      colour: '#CE7E07',
    },
    'faulty, damaged or broken part': {
      icon: 'screwdriver-wrench',
      colour: '#4F5061',
    },
    'faulty door or other part': {
      icon: 'screwdriver-wrench',
      colour: '#4F5061',
    },
    'filter issues': {
      icon: 'bars-filter',
      colour: '#7546C5',
    },
    'gas-related': {
      icon: 'fire-flame-simple',
      colour: '#0065C4',
    },
    'ice maker': {
      icon: 'icicles',
      colour: '#0065C4',
    },
    'noise and vibration': {
      icon: 'volume',
      colour: '#4F5061',
    },
    'power and electrical': {
      icon: 'bolt',
      colour: '#CE7E07',
    },
    'programs and controls': {
      icon: 'binary',
      colour: '#7546C5',
    },
    'spin cycle': {
      icon: 'washing-machine',
      colour: '#4F5061',
    },
    'unusual smells': {
      icon: 'nose',
      colour: '#CE7E07',
    },
    'washing performance': {
      icon: 'soap',
      colour: '#26A401',
    },
    'water and leaks': {
      icon: 'droplet',
      colour: '#0065C4',
    },
  };

  @HostListener('window:resize')
  onResize() {
    this.setOptionContainerSize();
  }

  isKeyboardNavigation = false;
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      this.isKeyboardNavigation = true;
    }
  }

  @HostListener('window:mousedown')
  onMouseDown() {
    this.isKeyboardNavigation = false;
  }

  autoSelectValue(controlName: string, value: any, form: UntypedFormGroup) {
    const control = form.get(controlName);
    if (control && !control.value) {
      control.setValue(value);
    }
  }

  onSelectionChanged(): void {
    if (this.showError) {
      this.showError = false;
    }
  }

  get showCustomLayout(): boolean {
    return !this.continueClicked;
  }

  ngOnInit(): void {
    this.claimTypeOptions = this.claimFormTypeHelperService.getClaimTypeData(
      this.claim
    )?.groups[0]?.radios?.radioGroup;
  }

  onContinueClick(
    form: UntypedFormGroup,
    selectionSummary: SelectionSummary[],
    claim
  ) {
    this.answerSubmitted = true;
    const claimTypeControl = form.get(FormTypes.CLAIMTYPEFORM);
    const applianceUsableControl = form.get(FormTypes.APPLIANCEUSABLE);
    const claimTypeValue = claimTypeControl?.value;
    const applianceUseableValue = applianceUsableControl?.value;

    if (!claimTypeValue) {
      this.showError = true;
      return;
    }

    this.formTypeChange(form, selectionSummary, claim);

    if (this.showUsabilityQuestion && !applianceUsableControl) {
      this.addUsabilityQuestion(form);
      this.answerSubmitted = false;
      setTimeout(() => {
        if (this.isKeyboardNavigation) {
          this.usabilityQuestion.nativeElement.focus();
        }
      });
      return;
    }

    if (applianceUsableControl && applianceUseableValue === null) {
      this.showError = true;
      return;
    }

    const payload = {
      ClaimTypeID: form.value.claimType,
      ...(this.showUsabilityQuestion
        ? { ApplianceUseable: form.value.ApplianceUseable }
        : {}),
    };

    if (!this.showInWarrantyNotice && form.valid) {
      this.claimTypeSelected.emit(payload);
    }
  }

  get showFreeTextSearchError(): boolean {
    return (
      (this.faultDataSearchForm.touched &&
        this.faultDataSearchForm.get('search').errors?.required) ||
      (this.formSubmitted &&
        this.faultDataSearchForm.get('search').errors?.noMatch)
    );
  }

  get isMobile() {
    return !!/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.claim) {
      this.showPreChatBanner = this.canShowPreChatBannerBasedOnClaim();
      this.cs.setClaim(this.claim);
    }
  }

  resetContinueState(formName: string): void {
    if (formName !== 'claimType') return;
    this.continueClicked = false;
    this.showUsabilityQuestion = false;
    this.answerSubmitted = false;
  }

  resetFormState(
    formName: string,
    selectionSummary: SelectionSummary[],
    claim: Claim,
    freeTextSearch: boolean
  ): void {
    this.resetContinueState(formName);
    this.resetStandardFaultCapture(formName);
    this.cs.resetForm(selectionSummary, formName, claim, freeTextSearch);
    this.faultDataSearchForm.reset();
    this.formSubmitted = false;
  }

  addUsabilityQuestion(form: UntypedFormGroup): void {
    form.addControl(
      'ApplianceUseable',
      new UntypedFormControl(null, Validators.required)
    );
    this.usabilityOptions =
      this.claimFormTypeHelperService.getApplianceUsabilityDataGroup()?.groups[0]?.radios?.radioGroup;
  }

  calculateOptionContainerHeight(optionSize: number, children: HTMLElement[]) {
    let height = 0;

    for (let i = 0; i < optionSize; i++) {
      height = height + children[i].offsetHeight;
    }

    height = height + 16 * (optionSize - 1) + 4;
    return height;
  }

  isGasLeak(selectionSummary: SelectionSummary[]) {
    return selectionSummary.find(
      (selection) =>
        selection.description.toLocaleLowerCase() ===
        'Gas leak'.toLocaleLowerCase()
    );
  }

  getFaultData(): Observable<Api.FaultData | undefined> {
    return this.cs.faultData$?.pipe(take(1));
  }

  setOptionContainerSize(): void {
    const optionSize = this.isMobile || window.innerWidth < 768 ? 5 : 7;

    setTimeout(() => {
      if (!this.optionsContainer?.nativeElement) return;
      const { children } = this.optionsContainer.nativeElement;

      if (children.length < optionSize) {
        this.optionsContainer.nativeElement.style.height = 'auto';
        return;
      }
      const height = this.calculateOptionContainerHeight(optionSize, children);
      this.optionsContainer.nativeElement.style.height = `${height}px`;
    });
  }

  searchFaults(): void {
    this.formSubmitted = true;
    this.faultDataSearchForm.markAllAsTouched();
    const searchTerm = this.faultDataSearchForm.get('search').value.trim();

    if (searchTerm === '') {
      this.faultDataSearchForm.get('search').setValue('');
    }

    this.cs.searchResults(searchTerm, () => {
      this.faultDataSearchForm.get('search').setErrors({
        noMatch: true,
      });
    });

    this.setOptionContainerSize();
  }

  selectResult(
    result: Api.FaultSearchResponse,
    selectionSummary: SelectionSummary[]
  ): void {
    const faultCategory = this.faultOptionsList.find(
      (i) => i.FaultCategoryName === result.faultId_lv2
    );

    const problemFaultId = faultCategory.PossibleAnswers.find(
      (answer) => `${answer.FaultCode}` === `${result.problemId_lv3}`
    )?.FaultID;
    const newSelectionSummary: SelectionSummary[] = [
      ...selectionSummary,
      {
        formName: FormTypes.FAULTAREAFORM,
        id: faultCategory?.FaultCategoryID as unknown as string,
        title: SummaryHeading.FAULTAREAFORM,
        description: result.faultId_descr,
      },
      {
        formName: FormTypes.FAULTTYPEFORM,
        id: `${problemFaultId}`,
        title: SummaryHeading.FAULTTYPEFORM,
        description: result.problemId_descr,
      },
    ];

    this.cs.updateSelectionSummary(newSelectionSummary);
    this.cs.updateForm({
      formData: null,
      form: null,
    });
    this.cs.showContinueCTA(true);
  }

  continue(form: UntypedFormGroup) {
    this.isIWEnabled = false;
    this.showInWarrantyNotice = false;
    this.showplanInWaitPeriodNotice = false;
  }

  formTypeChange(
    form: UntypedFormGroup,
    selectionSummary: SelectionSummary[],
    claim: Claim
  ) {
    if (form.controls[FormTypes.CLAIMTYPEFORM]) {
      const { isIWEnabled, showInWarrantyNotice, showplanInWaitPeriodNotice } =
        this.claimWarrantyStatus(form.value.claimType);

      this.isIWEnabled = isIWEnabled;
      this.showInWarrantyNotice = showInWarrantyNotice;
      this.showplanInWaitPeriodNotice = showplanInWaitPeriodNotice;
      this.showUsabilityQuestion =
        !this.showInWarrantyNotice &&
        claim?.getData?.DisplayApplianceUseable === true;

      if (
        this.showInWarrantyNotice ||
        this.showplanInWaitPeriodNotice ||
        this.isIWEnabled
      ) {
        this.triggerWarrantyNoticeGA();
        return;
      }
    } else {
      this.cs.formTypeChange(form, selectionSummary, claim);
    }
  }

  private triggerWarrantyNoticeGA() {
    this.analytics.pageViewEvent({
      eventClaims: 'rejected-in-warranty-dgx',
      claim: this.claim as Claim,
    });
  }

  routeToPlanDetails() {
    this.routing.routeToPlanDetails(this.claim as Claim);
  }

  routeToDashBoard() {
    this.routing.routeToDashBoard(this.claim as Claim);
  }

  claimWarrantyStatus(claimTypeID: string) {
    this.claimSelection = this.claim?.getData?.ClaimTypes.filter(
      (type, index) => {
        if (type.ClaimTypeID === claimTypeID) {
          this.position = index;
          return type.ClaimTypeID;
        }
      }
    )[0];

    const isIWEnabled = this.claimSelection?.IWEnabled || false;

    const showInWarrantyNotice = this.repairDetailsService.showInWarrantyNotice(
      this.claim as Claim,
      this.claimSelection?.ClaimTypeCode as string
    );
    const showplanInWaitPeriodNotice =
      this.repairDetailsService.showplanInWaitPeriodNotice(
        this.claim as Claim,
        this.claimSelection?.ClaimTypeCode as string
      );

    return {
      showInWarrantyNotice,
      showplanInWaitPeriodNotice,
      isIWEnabled,
    };
  }

  // -> standard fault capture logic

  selectedCategory: Api.FaultData | undefined = undefined;
  selectedFault: PossibleAnswer | undefined = undefined;

  setCategory(selectedCategory: Api.FaultData): void {
    this.selectedCategory = selectedCategory;
  }

  resetStandardFaultCapture(formName: string): void {
    if (formName === 'faultArea' || formName === 'claimType') {
      this.selectedCategory = undefined;
      this.selectedFault = undefined;
    }

    if (formName === 'faultType') {
      this.selectedFault = undefined;
    }
  }

  setFault(
    selectedFault: PossibleAnswer,
    selectionSummary: SelectionSummary[] = []
  ): void {
    this.selectedFault = selectedFault;
    const newSelectionSummary: SelectionSummary[] = [
      ...selectionSummary.filter((i) => i.formName === 'claimType'),
      {
        formName: FormTypes.FAULTAREAFORM,
        id: `${this.selectedCategory.FaultCategoryID}`,
        title: SummaryHeading.FAULTAREAFORM,
        description: `<ul class="bullet-list complete"><li><i class="fa-thin fa-check fa-lg success" style="margin-right: 8px;"></i>${this.selectedCategory.FaultCategoryName}</li></ul>`,
      },
      {
        formName: FormTypes.FAULTTYPEFORM,
        id: selectedFault.FaultID,
        title: SummaryHeading.FAULTTYPEFORM,
        description: `<ul class="bullet-list complete"><li><i class="fa-thin fa-check fa-lg success" style="margin-right: 8px;"></i>${selectedFault.FaultLabel}</li></ul>`,
      },
    ];

    this.cs.updateSelectionSummary(newSelectionSummary);
    this.cs.updateForm({
      formData: null,
      form: null,
    });
    this.cs.showContinueCTA(true);
  }

  // -> end of standard fault capture logic

  canShowPreChatBannerBasedOnClaim(): boolean {
    if (!this.claim) return false;
    this.productType = this.claim?.reflect?.productType.toLowerCase();
    if (this.productType.trim().includes('boiler')) {
      return true;
    }
    if (
      WhiteGoodApplianceNames.concat(FilterFixApplianceType).includes(
        this.productType
      )
    ) {
      return FilterFixApplianceType.includes(this.productType);
    }
    return false;
  }

  hideLiveChat(): void {
    this.chatService.hideChat();
  }

  showLiveChat(): void {
    this.chatService.showChat();
  }

  triggerSmartFixChatBot(mode: string): void {
    this.mode = mode;
    this.hideLiveChat();
    this.showSmartFixChatBot = true;
    this.trackSelectedFaultDetails();
    this.trackPreChatBannerChoice('yes', 'banner - smart fix');
  }

  preChatVisible(): void {
    this.trackPreChatBannerChoice('banner presented', 'banner - smart fix');
  }

  private handleSmartFixChat(): void {
    this.showSmartFixChatBot = false;
    this.chatService.hideSmartFixChatBubble();
    this.chatService.showChat();
    this.cs.submitClaim(true);
  }

  continueBooking(): void {
    this.handleSmartFixChat();
    this.trackSelectedFaultDetails();
    this.trackPreChatBannerChoice('No, continue booking', 'banner - smart fix');
  }

  continueBookingAfterSmartFixChat(): void {
    this.handleSmartFixChat();
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      "Right, let's continue", // to check with David!
      'fault-details-completed',
      this.claim,
      'fault-details-completed',
      'claims - smart fix'
    );
  }

  onChangeSmartFixChat(event: 'open' | 'closed'): void {
    this.smartFixChatState = event;
  }

  trackPreChatBannerChoice(eventLabel: string, eventAction: string) {
    this.smartFixAnalyticsService.triggerSmartFixEvent(
      eventLabel,
      'fault-details-completed',
      this.claim,
      eventAction,
      null
    );
  }

  trackSelectedFaultDetails() {
    this.cs.$selectionSummary.pipe(take(1)).subscribe((res) => {
      const faultArea = res?.find((res) => res.formName == 'faultArea');
      const faultType = res?.find((res) => res.formName == 'faultType');

      const formattedFaultDetails =
        [faultArea?.description, faultType?.description]
          .filter((value) => value)
          .join(' | ') || 'No Fault Details Captured';

      this.smartFixAnalyticsService.triggerSmartFixEvent(
        formattedFaultDetails,
        'fault-details-completed',
        this.claim,
        'fault-type-selected',
        'claims - smart fix'
      );
    });
  }
}
